import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useWindowSize } from '../../hooks/useWindowSize';
import { getNewsItems, deleteNewsItem } from '../../services/api';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Image, Typography } from 'antd'; // Keeping Antd's Image and Typography for specialized behavior
import DataTable from '../common/DataTable';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const NewsList = forwardRef(function NewsList({ onEdit }, ref) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { width } = useWindowSize();

  const fetchNews = async () => {
    setLoading(true);
    try {
      const data = await getNewsItems();
      // Pre-sort by date
      const sorted = [...data].sort((a, b) => moment(a.date).unix() - moment(b.date).unix());
      setItems(sorted);
    } catch (error) {
      toast.error('Failed to fetch news items');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNews();
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: fetchNews
  }));

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteNewsItem(deleteId);
      toast.success('News item deleted successfully');
      fetchNews();
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete news item');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getColumns = () => {
    const baseColumns = [
      {
        id: 'imageUrl',
        label: 'IMAGE',
        width: width < 768 ? '80px' : '150px',
        render: (row) => (
          <Image
            src={row.imageUrl}
            alt="News"
            width={width < 768 ? 60 : 100}
            height={width < 768 ? 40 : 65}
            style={{ objectFit: 'cover', borderRadius: '6px' }}
          />
        ),
      },
      {
        id: 'title',
        label: 'NEWS TITLE',
        render: (row) => (
          <Typography.Paragraph
            ellipsis={{ rows: width < 768 ? 1 : 2, tooltip: row.title }}
            style={{ 
              marginBottom: 0, 
              whiteSpace: 'normal', 
              wordBreak: 'break-word', 
              maxWidth: width < 768 ? '120px' : '220px',
              fontFamily: 'var(--primary-sans)',
              fontWeight: 600,
              color: 'var(--header-title)'
            }}
          >
            {row.title}
          </Typography.Paragraph>
        ),
      },
      {
        id: 'date',
        label: 'POSTED DATE',
        width: width < 768 ? '90px' : '140px',
        render: (row) => (
          <div style={{ color: 'var(--subtitle-text)', fontSize: '0.85rem', fontWeight: 500 }}>
            {moment(row.date).format(width < 768 ? 'MM/DD/YY' : 'MMM DD, YYYY')}
          </div>
        )
      },
      {
        id: 'actions',
        label: 'ACTIONS',
        align: 'right',
        width: width < 768 ? '100px' : '120px',
        render: (row) => (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton 
              onClick={() => onEdit(row)}
              size="small"
              sx={{ 
                color: '#94a3b8', 
                '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' }, 
                transition: 'all 0.2s'
              }}
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton 
              onClick={() => setDeleteId(row.id)}
              size="small"
              sx={{ 
                color: '#94a3b8', 
                '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' },
                transition: 'all 0.2s',
                ml: 1
              }}
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </div>
        ),
      },
    ];

    // Add content column only for desktop view
    if (width >= 768) {
      baseColumns.splice(3, 0, {
        id: 'content',
        label: 'PREVIEW CONTENT',
        width: '360px',
        render: (row) => (
          <Typography.Paragraph
            ellipsis={{ rows: 2, tooltip: row.content }}
            style={{ 
              marginBottom: 0, 
              whiteSpace: 'normal', 
              wordBreak: 'break-word', 
              maxWidth: '360px',
              color: 'var(--subtitle-text)',
              fontSize: '0.85rem'
            }}
          >
            {row.content}
          </Typography.Paragraph>
        ),
      });
    }

    return baseColumns;
  };

  return (
    <>
      <DataTable 
        premium={true}
        columns={getColumns()} 
        data={items} 
        loading={loading} 
        emptyTitle="No News Articles"
        emptySubtitle="There are no news items to display yet."
      />
      
      <DeleteConfirmDialog
        open={!!deleteId}
        onClose={() => !deleteLoading && setDeleteId(null)}
        onConfirm={confirmDelete}
        itemName="news item"
        loading={deleteLoading}
      />
    </>
  );
});

export default NewsList;
